import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import PassengerHistoryDialog from "@/components/PassengerHistoryDialog.tsx";
import { useParsePassengers } from "@/helpers/useParsePassengers";
import { usePlanActions, usePlanFilters, usePlanSearchItemId } from "@/stores/use-plan-table";
import { useTableDate } from "@/stores/use-table-date";
import { IPlanPassenger } from "@/types/tasks.types";

import CreateUserComp from "./components/CreateUser";
import DeletePassengerDialog from "./components/DeletePassengerDialog";
import PlanBlocker from "./components/PlanBlocker";
import PlanPanel from "./components/PlannPanel";
import ShowPassengerToWelcomeTeamDialog from "./components/ShowPassengerToWelcomeTeamDialog";
import UpdateCompanionsComp from "./components/UpdateCompanions";
import PlanTable from "./components/table";
import { CommonFilterSwitch } from "./types";

const PlannPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const searchItemId = usePlanSearchItemId();
    const date = useTableDate();
    const { passengers: allPassengers, loading, reloadTable } = useParsePassengers({ date, passengerId: searchItemId });
    const filters = usePlanFilters();
    const { updateShowPassengerHistoryDialog, updateSearchItemId } = usePlanActions();
    const api = useGridApiRef();

    useEffect(() => {
        const searchId = searchParams.get("searchItemId");
        if (!searchId) return;
        updateSearchItemId(searchId);
        const next = new URLSearchParams(searchParams);
        next.delete("searchItemId");
        setSearchParams(next, { replace: true });
    }, [searchParams, updateSearchItemId, setSearchParams]);

    const passengers: IPlanPassenger<"api">[] = useMemo(() => {
        if (!allPassengers) return [];
        else if (searchItemId) return allPassengers; // don't apply filters for specific passenger
        const { status, cancelled, isArrival, airport, hideSelfTransport, selectedDrivers } = filters;
        if (!status && !cancelled && !isArrival && !airport && !hideSelfTransport && !selectedDrivers.length)
            return allPassengers;
        return allPassengers.filter((passenger) => {
            return (
                (!isArrival || passenger.isFlightArrival === (isArrival === CommonFilterSwitch.FALSE ? false : true)) &&
                (!airport || passenger.airport === airport) &&
                (!status || passenger.status === status) &&
                (!cancelled || passenger.cancelled === (cancelled === CommonFilterSwitch.FALSE ? false : true)) &&
                (!hideSelfTransport || !passenger.isSelfTransport) &&
                (!selectedDrivers.length || (passenger.driverId && selectedDrivers.includes(passenger.driverId)))
            );
        });
    }, [filters, allPassengers, searchItemId]);

    useEffect(() => {
        const passengerId = searchParams.get("formattedId");
        if (!passengerId) return;

        updateShowPassengerHistoryDialog(passengerId);

        const next = new URLSearchParams(searchParams);
        next.delete("formattedId");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, updateShowPassengerHistoryDialog]);

    return (
        <PlanPanel apiRef={api}>
            <PlanTable
                reloadTable={reloadTable}
                rows={passengers}
                loading={loading}
                apiRef={api}
                allRows={allPassengers}
            />
            <CreateUserComp />
            <DeletePassengerDialog />
            <UpdateCompanionsComp />
            <ShowPassengerToWelcomeTeamDialog />
            <PlanBlocker />
            <PassengerHistoryDialog />
        </PlanPanel>
    );
};

export default PlannPage;
